import type { Point, PointArray } from '../types/contour-definition-array-type';

/**
 * Projects a contour onto a circle around its centroid.
 * The radius is based on the furthest point from centroid and can be shrunk
 * by a percentage so repeated calls progressively reduce the circle size.
 */
export function getRoundContour(
  pointArray: PointArray,
  centroid?: Point,
  shrinkPercent: number = 1,
): PointArray {
  if (pointArray.length < 2) return pointArray;

  const isClosed
    = pointArray.length > 0
      && pointArray[0].x === pointArray[pointArray.length - 1].x
      && pointArray[0].y === pointArray[pointArray.length - 1].y;

  const len = isClosed ? pointArray.length - 1 : pointArray.length;
  const points = pointArray.slice(0, len);
  if (points.length === 0) return pointArray;

  const center = centroid ?? { x: 0, y: 0 };

  let maxRadius = 0;
  points.forEach((point) => {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > maxRadius) {
      maxRadius = dist;
    }
  });

  if (maxRadius === 0) {
    return pointArray;
  }

  const clampedShrink = Math.max(0, Math.min(100, shrinkPercent));
  const radius = maxRadius * (1 - clampedShrink / 100);

  const rounded = points.map((point, index) => {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const angle
      = dx === 0 && dy === 0
        ? (index / Math.max(len, 1)) * Math.PI * 2
        : Math.atan2(dy, dx);

    return {
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
    };
  });

  if (isClosed && rounded.length > 0) {
    rounded.push({ ...rounded[0] });
  }

  return rounded;
}

// Alias kept for compatibility with existing naming requests.
export const getRoundCOntour = getRoundContour;
